import { RestaurantRole } from "@/store/AuthStore";

import type { OptionItem } from "../admin";
import type {
  ActionsForRole,
  AvailableSections,
  IUserRolePermissin,
} from "../admin/roleManagment/types";
import { UserLevel, UserRole } from "../admin/users";
import type { PaymentsMethods } from "./types/transactionsTypes";

export interface IRestaurant {
  firstName: string;
  lastName: string;
  role: RestaurantRole;
  email: string;
  phoneNumber: string;
  lastActivityAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  id: string;
}

export type AccessRoleType = {
  id: string;
  name: string;
  permissions: IUserRolePermissin[];
};

type Branch = {
  id: string;
  name: string;
  role: RestaurantRole.BRANCH | null;
  accessRole: AccessRoleType | null;
};

type Brand = {
  id: string;
  name: string;
  role: RestaurantRole.BRAND | null;
  accessRole: AccessRoleType | null;
  branches: Branch[];
};

type Group = {
  id: string;
  name: string;
  role: RestaurantRole.GROUP | null;
  accessRole: AccessRoleType | null;
  brands: Brand[];
};

export type AccessRoleTypeOption = {
  id: string;
  name: string;
  level: UserLevel;
  active: boolean;
  isSystem: boolean;
  permissions: {
    action: ActionsForRole;
    section: AvailableSections;
  }[];
  createdAt: string;
  updatedAt: string;
};

export type AssignmentsOptionsResponse = {
  groups: Group[];
};

export enum RestaurantOptionType {
  paymentMethods = "paymentMethods",
  Brands = "brands",
  Branches = "branches",
  Groups = "groups",
  AvailableRoles = "access-roles",
}

export interface RestaurantOptionsResponse {
  paymentMethods?: Array<PaymentsMethods>;
  brands: OptionItem[];
  branches: OptionItem[];
  groups: OptionItem[];
  accessRoles: AccessRoleTypeOption[];
}

export type StatisticsRequestKind =
  | "credits"
  | "transactions"
  | "reservations"
  | "totalCustomers"
  | "creditUsageChart"
  | "reservationsChart"
  | "fundingCreditLeftToGrant"
  | "fundingCreditGranted";

export interface StatisticsRequest {
  groupId?: string;
  brandId?: string;
  branchId?: string;
  statistic: StatisticsRequestKind[];
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

export interface StatisticsResponse {
  credits?: {
    balance: number;
    startingBalance?: number;
    currency: string;
  };
  transactions?: {
    total: number;
    currency: string;
    changePercent: number;
  };
  reservations?: {
    activeCount: number;
    changePercentFromYesterday: number;
    changePercentFromLastMonth: number;
  };
  totalCustomers?: {
    total: number;
    newCount: number;
    returnedCount: number;
  };
  creditUsageChart?: Array<{
    date: string;
    ideal?: number;
    actual: number;
  }>;
  reservationsChart?: Array<{
    date: string;
    value: number;
  }>;
  fundingCreditLeftToGrant?: {
    consumedBalance: number;
    remainingBalance: number;
    currency: string;
  };
  fundingCreditGranted?: {
    startingBalance: number;
    remainingBalance: number;
    currency: string;
    upcomingBalance: number;
  };
}

export const levelList = [
  { name: "Group", id: UserLevel.GROUP },
  { name: "Brand", id: UserLevel.BRAND },
  { name: "Branch", id: UserLevel.BRANCH },
];

export const rolesList = [{ name: "Admin", id: UserRole.ADMIN }];

export const levelMap: Record<RestaurantRole, UserLevel> = {
  [RestaurantRole.GROUP]: UserLevel.GROUP,
  [RestaurantRole.BRAND]: UserLevel.BRAND,
  [RestaurantRole.BRANCH]: UserLevel.BRANCH,
};
